import { useCallback, useState } from 'react'
import {
  IconChevronDown,
  IconChevronRight,
  IconChevronUp,
  IconShoppingCart,
} from '@tabler/icons-react'
import classNames from 'classnames'
import { Link } from '~/components'
import { Button } from '~/components/Button'
import { Image } from '~/components/Image'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { convertProductResourceUrl } from '~/helpers'
import {
  usePage,
  useFormatPrice,
  useCart,
  useCartDrawer,
  useAlgoliaSearchInsignts,
} from '~/hooks'
import { GTM, GTMEvent } from '~/lib'
import styles from './ProductDashboardVariantOptions.module.scss'
import { ProductDashboardVariantOptionsProps } from './ProductDashboardVariantOptions.types'

const getVariantCountLabel = (count: number) => {
  return `${count} ${count === 1 ? 'variant' : 'variants'}`
}

export const ProductDashboardVariantOptions = ({
  title,
  variants,
  skuLabel,
}: ProductDashboardVariantOptionsProps) => {
  const { siteCode } = usePage()
  const { formatPrice } = useFormatPrice()
  const { addToCart } = useCart()
  const { openCartDrawer } = useCartDrawer()
  const { latestQueryId, sendAddToCartEvent, removeQueryIDFromStorage } =
    useAlgoliaSearchInsignts()
  const [isOpen, setIsOpen] = useState(false)
  const [loadingSku, setLoadingSku] = useState<string | null>(null)
  const firstThumbnail = variants?.[0]?.thumbnail

  const handleAddToCart = useCallback(
    (sku: string) => {
      setLoadingSku(sku)
      addToCart.mutate(
        {
          addedItems: [{ sku, quantity: 1, algoliaQueryId: latestQueryId }],
        },
        {
          onSuccess(data) {
            if (data) {
              GTM.dispatch(GTMEvent.CLEAR_ECOMMERCE)
              GTM.dispatch(GTMEvent.CART_CONTENTS_PUSH, data.cart)
              sendAddToCartEvent(
                [sku],
                data.cart?.lineItems,
                data.cart?.subTotal,
                latestQueryId
              )
            }

            openCartDrawer()
            removeQueryIDFromStorage()
          },
          onSettled() {
            setLoadingSku(null)
          },
        }
      )
    },
    [
      addToCart,
      latestQueryId,
      openCartDrawer,
      removeQueryIDFromStorage,
      sendAddToCartEvent,
    ]
  )

  if (!variants?.length) {
    return null
  }

  return (
    <div className={classNames(styles.root, 'mt-4')}>
      <button
        type='button'
        className={styles.variantButtonToggle}
        aria-expanded={isOpen}
        onClick={() => {
          setIsOpen((prev) => {
            return !prev
          })
        }}>
        {firstThumbnail && (
          <Image
            src={convertProductResourceUrl(firstThumbnail.src, siteCode)}
            width={firstThumbnail.width}
            height={firstThumbnail.height}
            alt={firstThumbnail.altText}
            className={styles.thumbnail}
          />
        )}
        <span className={styles.titleBox}>
          <Typography
            tag={TypographyTag.span}
            variant={TypographyVariant.BodyRegularBold}
            className={styles.title}>
            {title}
          </Typography>
          <Typography
            tag={TypographyTag.span}
            variant={TypographyVariant.BodyRegular}
            className={styles.subtitle}>
            {getVariantCountLabel(variants?.length ?? 0)}
          </Typography>
        </span>
        {isOpen ? (
          <IconChevronUp size={20} color='var(--color-pdp-primary)' />
        ) : (
          <IconChevronDown size={20} color='var(--color-pdp-primary)' />
        )}
      </button>
      {isOpen && (
        <div className={styles.variantList}>
          {variants?.map((variant) => {
            const price = variant.finalPrice ?? variant.regularPrice

            return (
              <div key={variant.url} className={styles.variantOption}>
                {variant.thumbnail && (
                  <Link
                    href={variant.url}
                    className={styles.variantThumbnailLink}>
                    <Image
                      src={convertProductResourceUrl(
                        variant.thumbnail.src,
                        siteCode
                      )}
                      width={variant.thumbnail.width}
                      height={variant.thumbnail.height}
                      alt={variant.thumbnail.altText}
                      className={styles.variantThumbnail}
                    />
                  </Link>
                )}
                <div className={styles.variantInfo}>
                  <Link href={variant.url} className={styles.variantNameLink}>
                    <Typography
                      tag={TypographyTag.div}
                      variant={TypographyVariant.BodyRegularBold}
                      className={styles.variantName}>
                      {variant.title}
                    </Typography>
                  </Link>
                  {variant.sku && (
                    <Link href={variant.url} className={styles.variantSkuLink}>
                      <Typography
                        tag={TypographyTag.div}
                        variant={TypographyVariant.BodyRegular}
                        className={styles.variantSku}>
                        {skuLabel ? `${skuLabel} ${variant.sku}` : variant.sku}
                      </Typography>
                    </Link>
                  )}
                  <div className={styles.variantPriceRow}>
                    <Typography
                      tag={TypographyTag.div}
                      variant={TypographyVariant.BodyRegularBold}
                      className={styles.variantPrice}>
                      {formatPrice(price)}
                    </Typography>
                    <Button
                      size='sm'
                      className={styles.addToCartButton}
                      disabled={!variant.sku}
                      loading={loadingSku === variant.sku}
                      onClick={() => {
                        if (variant.sku && !loadingSku) {
                          handleAddToCart(variant.sku)
                        }
                      }}>
                      {loadingSku !== variant.sku && <IconShoppingCart />}
                      Add to Cart
                    </Button>
                  </div>
                </div>
                <Link href={variant.url} className={styles.variantChevron}>
                  <IconChevronRight
                    size={16}
                    color='var(--color-pdp-primary)'
                  />
                </Link>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
